import type { IAggFuncParams } from 'ag-grid-community';
import { WARNING_SEVERITY_TO_TEXT, WarningSeverity } from '../../../types';
import { getTypedEntries } from '../../../utils';
import type { PMSWarningColumnValue } from '../columns/pmsWarning';

/**
 * Aggregation function for PMS warning columns that collects all warnings,
 * calculates the highest severity, and formats warning messages by severity.
 *
 * Unlike warningAggFunc, this function aggregates warning messages into
 * formatted strings (e.g., "2 high warnings", "1 medium warning").
 */
export function pmsWarningAggFunc({
  values,
  rowNode,
}: IAggFuncParams<unknown, PMSWarningColumnValue | undefined>): PMSWarningColumnValue | undefined {
  // Collect all warnings and calc the highest severity to show on grouping
  const aggregateResult = values.reduce(
    (result, next) => {
      if (next == null || next.severity == null) {
        return result;
      }

      next.warnings.forEach(() => {
        result.messageBySeverityMap[next.severity] = (result.messageBySeverityMap[next.severity] ?? 0) + 1;
        result.severity = Math.max(result.severity, next.severity);
      });

      return result;
    },
    { messageBySeverityMap: {}, severity: WarningSeverity.NONE } as {
      messageBySeverityMap: { [P in WarningSeverity]: number };
      severity: WarningSeverity;
    }
  );

  const allMessages = getTypedEntries(aggregateResult.messageBySeverityMap)
    // sort highest to lowest
    .toSorted(([a], [b]) => Number(b) - Number(a))
    .reduce((acc, [key, value]) => {
      if (Number(key) > 0) {
        acc.push(`${value} ${WARNING_SEVERITY_TO_TEXT[key]} warning${value > 1 ? 's' : ''}`);
      }
      return acc;
    }, [] as string[]);
  return allMessages.length === 0
    ? undefined
    : {
        severity: aggregateResult.severity,
        nodeId: rowNode?.id ?? '',
        warnings: allMessages,
      };
}
